// Complex Morlet continuous wavelet transform via FFT.
//
// Why Morlet: it is the standard go-to wavelet for audio time-frequency
// analysis — a complex sinusoid windowed by a Gaussian, well-localized in
// both time and frequency, with a clear "this frequency at this time"
// interpretation. ω₀ = 6 is the conventional center frequency (cmor1-1
// equivalent) — high enough for the admissibility approximation to hold,
// low enough to keep reasonable time resolution.
//
// Implementation: FFT-based per scale, O(N·S·log N) total. Plenty fast for
// per-cycle ICA components (< 10 K samples × 60 scales).

export const WAVELET_NAME = 'Complex Morlet (ω₀ = 6)';
export const MORLET_OMEGA0 = 6.0;

/** In-place radix-2 Cooley-Tukey FFT. Length must be a power of 2. */
function fftInplace(real: Float64Array, imag: Float64Array): void {
  const n = real.length;
  // Bit-reversal permutation
  for (let i = 1, j = 0; i < n; i++) {
    let b = n >> 1;
    while (j >= b) { j -= b; b >>= 1; }
    j += b;
    if (i < j) {
      const tr = real[i]; real[i] = real[j]; real[j] = tr;
      const ti = imag[i]; imag[i] = imag[j]; imag[j] = ti;
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const angleStep = -2 * Math.PI / size;
    for (let start = 0; start < n; start += size) {
      for (let k = 0; k < half; k++) {
        const angle = angleStep * k;
        const wr = Math.cos(angle);
        const wi = Math.sin(angle);
        const i1 = start + k;
        const i2 = i1 + half;
        const tr = real[i2] * wr - imag[i2] * wi;
        const ti = real[i2] * wi + imag[i2] * wr;
        real[i2] = real[i1] - tr;
        imag[i2] = imag[i1] - ti;
        real[i1] += tr;
        imag[i1] += ti;
      }
    }
  }
}

export interface CWTResult {
  /** numScales × numTimes flat (row-major). Squared magnitude of the CWT. */
  power: Float32Array;
  numScales: number;
  numTimes: number;
  /** Center frequency in Hz for each scale row, descending or ascending
   *  depending on input scales array. */
  freqsHz: Float64Array;
}

/**
 * Compute |Morlet CWT|² of `signal` at the given scales.
 *
 * Center frequency for scale a (with ω₀ = 6) is f = ω₀·fs / (2π·a).
 */
export function morletCWT(
  signal: Float32Array,
  scales: Float64Array,
  sampleRate: number,
): CWTResult {
  const n = signal.length;
  let nfft = 1;
  while (nfft < n) nfft <<= 1;

  // FFT of the (zero-padded) signal once.
  const sigR = new Float64Array(nfft);
  const sigI = new Float64Array(nfft);
  for (let i = 0; i < n; i++) sigR[i] = signal[i];
  fftInplace(sigR, sigI);

  // Angular frequencies (cycles 0..nfft/2 positive, then negative).
  const omega = new Float64Array(nfft);
  for (let k = 0; k < nfft; k++) {
    omega[k] = k <= nfft / 2
      ? (2 * Math.PI * k) / nfft
      : (2 * Math.PI * (k - nfft)) / nfft;
  }

  const numScales = scales.length;
  const power = new Float32Array(numScales * n);
  const freqsHz = new Float64Array(numScales);
  const tR = new Float64Array(nfft);
  const tI = new Float64Array(nfft);

  for (let s = 0; s < numScales; s++) {
    const a = scales[s];
    freqsHz[s] = (MORLET_OMEGA0 * sampleRate) / (2 * Math.PI * a);

    // Build wavelet in frequency domain and multiply against the signal's
    // spectrum. Analytic Morlet has zero negative-frequency content.
    for (let k = 0; k < nfft; k++) {
      if (omega[k] < 0) {
        tR[k] = 0;
        tI[k] = 0;
        continue;
      }
      const arg = a * omega[k] - MORLET_OMEGA0;
      const w = Math.sqrt(2 * Math.PI * a) * Math.exp(-(arg * arg) / 2);
      tR[k] = sigR[k] * w;
      tI[k] = sigI[k] * w;
    }
    // Inverse FFT via conjugate-FFT-conjugate / nfft.
    for (let k = 0; k < nfft; k++) tI[k] = -tI[k];
    fftInplace(tR, tI);
    for (let k = 0; k < nfft; k++) tI[k] = -tI[k];

    const base = s * n;
    const inv = 1 / nfft;
    for (let i = 0; i < n; i++) {
      const r = tR[i] * inv;
      const im = tI[i] * inv;
      power[base + i] = r * r + im * im;
    }
  }

  return { power, numScales, numTimes: n, freqsHz };
}

/**
 * Build a log-spaced array of scales spanning the given frequency band.
 * Returns scales in DESCENDING order (so the first row corresponds to the
 * HIGHEST frequency — the natural top-down orientation for a heatmap).
 */
export function logScales(
  fMinHz: number,
  fMaxHz: number,
  numScales: number,
  sampleRate: number,
): Float64Array {
  // f = ω₀·fs / (2π·a)  ⇒  a = ω₀·fs / (2π·f)
  const aMin = (MORLET_OMEGA0 * sampleRate) / (2 * Math.PI * fMaxHz);
  const aMax = (MORLET_OMEGA0 * sampleRate) / (2 * Math.PI * fMinHz);
  const out = new Float64Array(numScales);
  const logMin = Math.log(aMin);
  const logMax = Math.log(aMax);
  for (let i = 0; i < numScales; i++) {
    out[i] = Math.exp(logMin + ((logMax - logMin) * i) / (numScales - 1));
  }
  return out;
}
